import type {
  CompetitorInsight,
  ProjectVisibilityBrandComparison,
  ProjectVisibilityTopBrand,
} from '../types';

/** Competitors shown on dashboard cards before "مشاهده همه". */
export const DASHBOARD_COMPETITOR_PREVIEW = 5;

const normalizeHost = (value: string | null | undefined) =>
  (value ?? '').toLowerCase().replace(/^www\./, '');

export function matchesTrackedCompetitor(
  row: Pick<ProjectVisibilityTopBrand, 'canonicalName' | 'domain' | 'knownBrandId'>,
  tracked: CompetitorInsight,
): boolean {
  const name = row.canonicalName.toLowerCase();
  const host = normalizeHost(row.domain);
  return (
    name === tracked.brandName.toLowerCase() ||
    tracked.aliases.some((alias) => alias.name.toLowerCase() === name) ||
    (host !== '' && host === normalizeHost(tracked.host))
  );
}

export function previewCompetitors<T>(
  items: T[],
  limit = DASHBOARD_COMPETITOR_PREVIEW,
): T[] {
  return items.slice(0, limit);
}

export function canViewAllCompetitors(
  appeared: number,
  tracked: number,
): boolean {
  return appeared > 0 || tracked > 0;
}

function trackedAsZeroRow(tracked: CompetitorInsight): ProjectVisibilityTopBrand {
  return {
    knownBrandId: null,
    canonicalName: tracked.brandName,
    domain: tracked.host || null,
    faviconUrl: null,
    relationship: 'competitor',
    aiMentions: 0,
    promptCoverage: 0,
    avgPosition: null,
    visibilityScore: 0,
    changePercentage: null,
    shareOfVoice: 0,
  };
}

/** Appeared competitors first, then tracked ones that have no mentions yet. */
export function allEnteredCompetitors(
  competitors: ProjectVisibilityTopBrand[],
  tracked: CompetitorInsight[],
): ProjectVisibilityTopBrand[] {
  const missing = tracked.filter(
    (item) => !competitors.some((row) => matchesTrackedCompetitor(row, item)),
  );
  return [...competitors, ...missing.map(trackedAsZeroRow)];
}

export function competitorBrandKey(
  brand: Pick<ProjectVisibilityTopBrand, 'knownBrandId' | 'canonicalName'>,
): string {
  return brand.knownBrandId ?? brand.canonicalName;
}

export function padComparisonBrands(
  brands: ProjectVisibilityBrandComparison[],
  entered: ProjectVisibilityTopBrand[],
): ProjectVisibilityBrandComparison[] {
  const existing = new Set(
    brands.map((brand) => competitorBrandKey(brand).toLowerCase()),
  );
  const extra = entered
    .filter((row) => !existing.has(competitorBrandKey(row).toLowerCase()))
    .map((row) => ({
      knownBrandId: row.knownBrandId,
      canonicalName: row.canonicalName,
      relationship: row.relationship,
      aiMentions: row.aiMentions,
      visibilityScore: row.visibilityScore,
      shareOfVoice: row.shareOfVoice,
    }));
  return [...brands, ...extra];
}